import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Clock, Download, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRAttendance = () => {
  const { getAuthHeader } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
  const [report, setReport] = useState(null);

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/attendance/all`, {
        headers: getAuthHeader(),
        params: { month: selectedMonth }
      });
      setAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/attendance/report`, {
        headers: getAuthHeader(),
        params: { month: selectedMonth }
      });
      setReport(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const exportToCSV = () => {
    if (!report) return;
    
    const csvRows = [
      ['Employee ID', 'Employee Name', 'Department', 'Working Days', 'Present', 'Absent', 'Half Day', 'Leave'].join(','),
      ...report.report.map(r => 
        [r.employee_id, r.employee_name, r.department, r.working_days, r.present, r.absent, r.half_day, r.leave].join(',')
      )
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${selectedMonth}.csv`;
    a.click();
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      half_day: 'bg-yellow-100 text-yellow-800',
      leave: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div>Loading attendance...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Clock size={28} />
            Attendance Management
          </h2>
          <p className="text-gray-600 mt-1">View and export employee attendance</p>
        </div>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Select Month</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <Button onClick={generateReport} variant="outline">
              <Calendar size={18} className="mr-2" />
              Generate Report
            </Button>
            {report && (
              <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700">
                <Download size={18} className="mr-2" />
                Export CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Report */}
      {report && (
        <Card className="border-primary">
          <CardHeader className="bg-primary/5">
            <CardTitle>Attendance Summary - {report.month}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {report.report.map((emp) => (
                <div key={emp.employee_id} className="grid md:grid-cols-7 gap-4 p-4 border rounded-lg">
                  <div className="md:col-span-2">
                    <div className="font-bold">{emp.employee_name}</div>
                    <div className="text-sm text-gray-600">{emp.employee_id} • {emp.department}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{emp.present}</div>
                    <div className="text-xs text-gray-600">Present</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{emp.absent}</div>
                    <div className="text-xs text-gray-600">Absent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{emp.half_day}</div>
                    <div className="text-xs text-gray-600">Half Day</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{emp.leave}</div>
                    <div className="text-xs text-gray-600">Leave</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{emp.working_days}</div>
                    <div className="text-xs text-gray-600">Total Days</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Attendance Records */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Attendance - {selectedMonth}</CardTitle>
        </CardHeader>
        <CardContent>
          {attendance.length > 0 ? (
            <div className="space-y-3">
              {attendance.slice(0, 50).map((record, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{record.employee_name}</div>
                    <div className="text-sm text-gray-600">
                      {record.employee_id} • {record.date}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {record.check_in && (
                      <div className="text-sm">
                        <span className="text-gray-600">In:</span> <span className="font-medium">{record.check_in}</span>
                      </div>
                    )}
                    {record.check_out && (
                      <div className="text-sm">
                        <span className="text-gray-600">Out:</span> <span className="font-medium">{record.check_out}</span>
                      </div>
                    )}
                    <Badge className={getStatusColor(record.status)}>
                      {record.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </div>
              ))}
              {attendance.length > 50 && (
                <p className="text-center text-gray-500 py-4">
                  Showing 50 of {attendance.length} records. Generate report to see all.
                </p>
              )}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500">No attendance records for this month</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HRAttendance;